import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { handleLogin } from "../../actions/Authentication/Auth_SignIn_SignUp";
import { getLocalSessionData } from "../../utils/Authentication/handleUserSession";
import { 
    LOCALSESSIONDATAKEYS, 
    NATIVE_APP_SELECTION_ATTRIBUTE, 
    NATIVE_APP_AUTHENTICATION_FLAG, 
    NATIVE_APP_SUBSCRIPTION_ATTRIBUTE 
} from "../../constants/AppConstants";
import { 
    buildSkipRegistrationQSNativeApp, 
    buildOneBenefitPageQSNAtiveApp, 
    getNativeAppQueryParamsValuesByKey, 
    isBplusRegistrationProcess, 
    isEcoproRegistrationProcess, 
    updateNativeAppQueryParams_LocalStorage 
} from "../../constants/AppMethod";
import ErrorPage from "../../components/shared/errorPage/ErrorPage";
import { nativeAppRoutePaths, routePath } from "../../constants/Routes";
import { FetchUserAccount } from "../../actions/UserAccount";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import Loader from "../../components/shared/loader/Loader";
import { useIssueBOLToken } from "../../utils/CustomHooks/useIssueBOLToken";
import { useTranslation } from "react-i18next";

const NoTokenHandler = React.memo(() => {
    const { t } = useTranslation(["translation"]);
    const dispatch = useDispatch();
    const history = useHistory();
    const { 
        fetchToken, 
        BOLApiToken_error,
        BOLApiToken_success,
        BOLApiToken_data
    } = useIssueBOLToken();
    const IS_BPLUS_REGISTRATION_SERVICE = isBplusRegistrationProcess();
    const IS_ECOPRO_REGISTRATION_SERVICE = isEcoproRegistrationProcess();
    const UserAuthInfo = useSelector(state => state.App_SignIn_Auth);
    const {
        userDeviceList_error,
        userDeviceList_success
    } = useSelector(state => state.ROOTREDUCER_RTK.GetUserEnrolledDevicesService);
    const UserAccount = useSelector((state) => state.UserAccount);
    const adb2cId = getLocalSessionData(LOCALSESSIONDATAKEYS.AZURE_AD_B2C_UID);
    var userId = getLocalSessionData(LOCALSESSIONDATAKEYS.UID);
    var deviceId = getLocalSessionData(LOCALSESSIONDATAKEYS.DEVICE_ID);
    var signupType = getLocalSessionData(LOCALSESSIONDATAKEYS.SIGNUP_TYPE);
    const authTokenInfo = JSON.parse(getLocalSessionData(LOCALSESSIONDATAKEYS.NATIVE_APP_AUTH_TOKEN_INFO));
    const isAuthTokenInfoExists = authTokenInfo?.includes('token');
    const serviceTypeSelected = parseInt(getNativeAppQueryParamsValuesByKey("selection"));
    const SKIP_TYPES = {'REGISTRATION': 'SKIP_REGISTRATION', 'SERVICE': 'SKIP_SERVICE_ENROLLMENT'};
 
    /**
     * Trigger the flow required on mount
     */
    useEffect(() => {
        /* In native app (OnMount)
        *     - If it's purely an authentication process, no further API calls are needed,
        *       and the user is redirected to the native app page once authenticated.
        *     - If additional processing is needed (not just authentication),
        *       the flow continues with the necessary API calls.
        * */
        if(serviceTypeSelected === NATIVE_APP_SELECTION_ATTRIBUTE.SKIP){
            if(!isAuthTokenInfoExists){
                fetchToken();
            } else {
                handleAppRedirection()
            }
        } else {
            if(IS_BPLUS_REGISTRATION_SERVICE || IS_ECOPRO_REGISTRATION_SERVICE){
                dispatch(FetchUserAccount(adb2cId, userId, deviceId, signupType));
            } else {
                dispatch(handleLogin(signupType))
            }
        }
        // eslint-disable-next-line
    }, []);

    /**
     * Handle fetchToken() success or failure
     */
    useEffect(() => {
        if(BOLApiToken_success){
            if(BOLApiToken_data.is_retry){
                fetchToken();
            } else if(serviceTypeSelected === NATIVE_APP_SELECTION_ATTRIBUTE.SKIP) {
                handleAppRedirection();
            } else if(!UserAuthInfo.BOC_NO_UserID_Err && userDeviceList_success) {
                dispatch(FetchUserAccount(adb2cId, userId, deviceId, signupType));
            } else {
                dispatch(handleLogin(signupType));
            }
        }
        // eslint-disable-next-line
    }, [BOLApiToken_success, BOLApiToken_data])

    /**
     * Handle FetchUserAccount() API success or failure
    */
    useEffect(() => {
        if(UserAccount.success){
            if(IS_BPLUS_REGISTRATION_SERVICE){
                history.push(nativeAppRoutePaths.BPLUS.REGISTER_SERVICE);
            } else if (IS_ECOPRO_REGISTRATION_SERVICE) {
                history.push(routePath.PROCESS_DEVICE);
            } else {
                history.push(routePath.ROLE_REDIRECTION);
            }
        }
        // eslint-disable-next-line
    }, [UserAccount.success]);

    
    /**
     * Handle handleLogin() API success or failure
     */
    useEffect(() => {
        /**
         * - In native apps, even though the UID is returned User/GetUserBocIdByADB2CId API response,
         *   an API call is required to generate a valid session token, only if login_token is not available.
         */
        if (UserAuthInfo.success) {
            if(!UserAuthInfo.BOC_NO_UserID_Err && userDeviceList_success) {
                if(!isAuthTokenInfoExists){
                    fetchToken();
                } else {
                    dispatch(FetchUserAccount(adb2cId, userId, deviceId, signupType));
                }
            } else if(UserAuthInfo.BOC_NO_UserID_Err) {
                fetchToken();
            }
        }
        // eslint-disable-next-line
    }, [
        UserAuthInfo.success, 
        UserAuthInfo.BOC_NO_UserID_Err, 
        userDeviceList_success
    ])

    const handleAppRedirection = () => {
        let QS = null;
          const type = (serviceTypeSelected === NATIVE_APP_SELECTION_ATTRIBUTE.SKIP) 
          ? SKIP_TYPES.SERVICE 
          : SKIP_TYPES.REGISTRATION;
        if(type === SKIP_TYPES.SERVICE){
            QS = buildOneBenefitPageQSNAtiveApp(NATIVE_APP_SUBSCRIPTION_ATTRIBUTE.SKIP, NATIVE_APP_SUBSCRIPTION_ATTRIBUTE.SKIP);
        } else {
            QS = buildSkipRegistrationQSNativeApp(NATIVE_APP_AUTHENTICATION_FLAG.HAS_COMPLETED_AUTH);
        }
        updateNativeAppQueryParams_LocalStorage(null, QS, LOCALSESSIONDATAKEYS.NATIVE_APP_AUTH_TOKEN_INFO);
        history.push(nativeAppRoutePaths.WEB_TO_APP_REDIRECTION);
    }

    if (UserAccount.error || BOLApiToken_error || userDeviceList_error || UserAuthInfo.error) {
        return <ErrorPage errorInfo={
                    UserAccount.error 
                    || BOLApiToken_error 
                    || userDeviceList_error 
                    || UserAuthInfo.error 
                    || t('ERROR_CONTENT.ERR_INFO.TECHNICAL_ERROR')}>
                </ErrorPage>
    }
    return (
        <Loader />
    )
});

export default NoTokenHandler;